<template>
  <section>
    <errors-list :errorMessage="errorMessage" />
    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <!-- Status column is a custom component, not using GridCell for it -->
          <td class="align-center layout pr-0">
            <v-icon class="mr-1" :color="getStatusColor(props.item.status)">
              {{ getStatusIcon(props.item.status) }}
            </v-icon>
            <v-flex class="capitalize">{{ props.item[translatedHeaders[0].value] }}</v-flex>
          </td>

          <grid-cell
            :header="header"
            :row="props.item"
            v-for="(header, index) in translatedHeaders.slice(1)"
            :key="index"
            @action="onAction"
          ></grid-cell>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="modal" persistent lazy full-width width="390px">
      <debt-form
        :key="selectedDebt ? selectedDebt.id : selectedDebt"
        :debt="selectedDebt"
        @close-modal="closeModal"
      />
    </v-dialog>
  </section>
</template>

<style>
.capitalize {
  text-transform: capitalize;
}
</style>

<script>
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import {
  defaultCostFormatter,
  defaultDateFormatter,
  defaultDateTimeFormatter,
  serializeQuery,
} from '../../utils';
import DebtForm from './DebtForm';

export const defaultHeaders = [
  { text: '@debtsList/statusColumnHeader', value: 'status' },
  { text: '@debtsList/paymentStatus', value: 'paymentStatus', width: 32, sortable: false },
  { text: '@debtsList/periodFromColumnHeader', value: 'periodFrom', format: defaultDateFormatter },
  { text: '@debtsList/periodTillColumnHeader', value: 'periodTill', format: defaultDateFormatter },
  { text: '@debtsList/periodCostColumnHeader', value: 'periodCost', format: defaultCostFormatter },
  {
    text: '@debtsList/scheduleStatusColumnHeader',
    value: 'schedule.status',
  },
  {
    text: '@debtsList/createdAtColumnHeader',
    value: 'createdAt',
    format: defaultDateTimeFormatter,
  },
];

const paidInvoiceStatuses = ['processed', 'approved'];

export default {
  components: { GridCell, DebtForm, ErrorsList },

  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
    pickerDate: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      items: [],
      selectedDebt: null,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'periodFrom',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
      modal: false,
      loading: false,
      errorMessage: '',
    };
  },

  watch: {
    pagination() {
      this.fetchData();
    },

    filters() {
      this.fetchData();
    },

    pickerDate() {
      this.fetchData();
    },
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },
    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        ...this.filters,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy =
          this.pagination.sortBy === 'schedule.status'
            ? this.pagination.sortBy
            : `debt.${this.pagination.sortBy}`;
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
      } else {
        delete query.sortBy;
        delete query.sortOrder;
      }

      if (!query.from) {
        query.from = '1970-01-01T00:00:00.000Z';
      }
      return query;
    },
  },

  methods: {
    onAction({ action, row }) {
      if (!action) {
        this.selectedDebt = row;
        this.modal = true;
      }
    },

    async closeModal(isSent = false) {
      this.modal = false;

      if (isSent) {
        await this.fetchData(true);
      }
      this.selectedDebt = null;
    },

    async fetchData(force) {
      const qs = serializeQuery(this.query);
      if (!force && (this.lastQueryString === qs || this.loading)) {
        return;
      }

      try {
        this.loading = true;
        this.$emit('beforeRequest', this.query);
        const {
          data: {
            data,
            meta: { total },
          },
        } = await this.$http.get(`/api/debts?${qs}`);

        this.items = data.map((debt) => {
          if (!debt || !debt.status) {
            return debt;
          }

          debt.paymentStatus = false;

          if (debt.status === 'processed') {
            const { paperInvoice, bgcInvoice } = debt;
            if (bgcInvoice) {
              debt.paymentStatus = paidInvoiceStatuses.includes(bgcInvoice.status);
            }
            if (paperInvoice && !debt.paymentStatus) {
              debt.paymentStatus = paidInvoiceStatuses.includes(paperInvoice.status);
            }
          }

          const translatedStatus = this.$t(`@debtForm/${debt.status}StatusLabel`) || debt.status;
          return { ...debt, translatedStatus };
        });
        this.pagination.totalItems = total;
        this.lastQueryString = qs;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    getStatusIcon(status) {
      if (status === 'planned') {
        return 'watch_later';
      } else if (status === 'terminated') {
        return 'error';
      } else if (status === 'processed') {
        return 'check_circle';
      } else {
        return 'help';
      }
    },

    getStatusColor(status) {
      if (['planned', 'postponed'].includes(status)) {
        return 'yellow darken-3';
      } else if (status === 'processed') {
        return 'light-green darken-3';
      } else if (status === 'terminated') {
        return 'red darken-2';
      } else {
        return 'grey';
      }
    },
  },
};
</script>
